import { Box, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { useBottomDrawer } from 'core/stores/bottomDrawer/hooks';
import { useUpdateCandidateBatchMutation } from 'generated-graphql/hooks/apollo-hooks';
import {
  CandidateBatchGroup,
  CandidateBatchStatus
} from 'generated-graphql/types';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { useLoadingOverlay } from 'services/loading/hooks';
import { ConfirmationModalType } from 'services/modals/constants';
import { useConfirmationModal } from 'services/modals/hooks';
import { ToastType, useToast } from 'services/toast';
import { SearchCandidateBatchesItem } from '../types/SearchCandidateBatchesItem';

const boxContainer: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '20px'
};

const buttonSx: SxProps = {
  height: 56,
  padding: '0 24px 0 24px',
  display: 'flex',
  gap: '4px',
  flexShrink: 0
};

type Props = {
  candidateBatchIdsSelected: string[];
  candidateBatchesSelected: SearchCandidateBatchesItem[];
  onSuccess?: () => void;
  onApplyRulesetSuccess?: (
    candidateBatchGroup: Pick<CandidateBatchGroup, 'id' | 'name'>,
    candidateBatchIds: string[]
  ) => void;
};

export const AnalysesListActionsBottomDrawer: React.FC<Props> = ({
  candidateBatchIdsSelected,
  candidateBatchesSelected,
  onSuccess,
  onApplyRulesetSuccess
}) => {
  const { closeBottomDrawer, onClose } = useBottomDrawer();
  const [updateCandidateBatch] = useUpdateCandidateBatchMutation();
  const { showModal } = useConfirmationModal();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showToast } = useToast();
  const { openDrawer } = useDrawer();

  const analysesCount = `${candidateBatchIdsSelected.length} analys${
    candidateBatchIdsSelected.length > 1 ? 'es' : 'is'
  }`;

  const draftAnalyses = candidateBatchesSelected.filter(
    (batch) => batch.status === CandidateBatchStatus.Draft
  );
  const nonDraftAnalyses = candidateBatchesSelected.filter(
    (batch) => batch.status !== CandidateBatchStatus.Draft
  );
  const allAreDrafts = draftAnalyses.length === candidateBatchesSelected.length;
  const hasDrafts = draftAnalyses.length > 0;
  const hasWorkflowManaged = candidateBatchesSelected.some(
    (batch) => batch.isWorkflowManaged
  );

  if (!candidateBatchIdsSelected.length) return null;

  const handleArchive = async () => {
    try {
      showLoadingOverlay();
      await Promise.all(
        candidateBatchIdsSelected.map(async (id) => {
          await updateCandidateBatch({
            variables: {
              input: {
                id,
                status: CandidateBatchStatus.Archived
              }
            }
          });
        })
      );
      await onSuccess?.();
      showToast({
        title: 'Analyses archived',
        type: ToastType.Success
      });
      onClose?.();
    } finally {
      hideLoadingOverlay();
    }
  };

  const handleConfirmation = () => {
    showModal({
      title: `Archive ${analysesCount}?`,
      message:
        'Once archived, these analyses will be stopped for all applicants. Archived analyses cannot be re-activated in the future.',
      modalType: ConfirmationModalType.DESTRUCTIVE,
      onDestructiveConfirm: handleArchive,
      destructiveText: 'Archive',
      cancelText: 'Keep'
    });
  };

  const handleApplyRuleset = () => {
    const nonDraftIds = nonDraftAnalyses.map((batch) => batch.id);

    openDrawer({
      drawerContentType: DrawerComponentType.RulesetDrawer,
      drawerComponentProps: {
        candidateBatchIds: nonDraftIds,
        hasDrafts,
        onSuccess: (
          candidateBatchGroup: Pick<CandidateBatchGroup, 'id' | 'name'>
        ) => {
          onApplyRulesetSuccess?.(candidateBatchGroup, nonDraftIds);

          onClose?.();
        }
      }
    });
  };

  return (
    <Box sx={boxContainer}>
      <Typography
        variant="body2"
        width="140px"
        textAlign="right"
        flexShrink={0}
      >
        {`${candidateBatchIdsSelected.length} analys${
          candidateBatchIdsSelected.length > 1 ? 'es' : 'is'
        } selected`}
      </Typography>
      {!hasWorkflowManaged && (
        <>
          <Button
            variant="secondary"
            sx={buttonSx}
            onClick={handleApplyRuleset}
            disabled={allAreDrafts}
          >
            Apply ruleset
          </Button>
          <Button
            state="destructive"
            sx={buttonSx}
            onClick={handleConfirmation}
          >
            Archive
          </Button>
        </>
      )}
      <Button variant="tertiary" sx={buttonSx} onClick={closeBottomDrawer}>
        Cancel
      </Button>
    </Box>
  );
};
